import { useRef, useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const softwares = [
  {
    name: 'KuzeyPOS Restoran',
    logo: 'ri-restaurant-line',
    category: 'Restoran Yönetimi',
    desc: 'Restoran, kafe ve yemek işletmeleri için tam entegre otomasyon sistemi. Masa yönetimi, mutfak ekranı, adisyon ve raporlama tek platformda.',
    features: ['Masa Yönetimi', 'Mutfak Ekranı', 'Adisyon Sistemi', 'Stok Takibi', 'Detaylı Raporlama'],
    color: 'from-orange-400 to-red-500',
    image: 'https://public.readdy.ai/ai/img_res/b9e57ab130b6b0c1002322c2c7fb4cef.jpg',
  },
  {
    name: 'KuzeyPOS Hızlı Satış',
    logo: 'ri-store-2-line',
    category: 'Perakende Satış',
    desc: 'Market, bakkal ve perakende satış noktaları için hızlı satış sistemi. Barkod okuma, hızlı satış ve stok yönetimi.',
    features: ['Barkod Okuma', 'Hızlı Satış', 'Stok Yönetimi', 'Müşteri Takibi', 'Çoklu Ödeme'],
    color: 'from-emerald-400 to-teal-500',
    image: 'https://readdy.ai/api/search-image?query=retail%20POS%20software%20cashier%20system%20modern%20screen%20professional%20clean%20interface%20store&width=600&height=400&seq=302&orientation=landscape',
  },
  {
    name: 'TBS - Vize Takip',
    logo: 'ri-global-line',
    category: 'Kurumsal Yazılım',
    desc: 'Uluslararası vize başvurularını yönetin, şubeli yapılar için merkezi kontrollü platform. Başvuru takibi ve belge yönetimi.',
    features: ['Vize Takibi', 'Belge Yönetimi', 'Şube Yönetimi', 'Müşteri Portalı', 'Otomatik Bildirim'],
    color: 'from-sky-400 to-blue-500',
    image: 'https://readdy.ai/api/search-image?query=visa%20application%20management%20software%20dashboard%20modern%20professional%20clean%20interface%20corporate&width=600&height=400&seq=303&orientation=landscape',
  },
  {
    name: 'HanSoft ERP',
    logo: 'ri-building-line',
    category: 'ERP Çözümü',
    desc: 'İşletmenizin tüm süreçlerini tek platformda yönetin. Muhasebe, insan kaynakları, üretim ve lojistik entegrasyonu.',
    features: ['Muhasebe Entegrasyonu', 'İK Yönetimi', 'Üretim Takibi', 'Lojistik', 'Bulut Tabanlı'],
    color: 'from-violet-400 to-purple-500',
    image: 'https://readdy.ai/api/search-image?query=ERP%20enterprise%20software%20dashboard%20analytics%20modern%20professional%20clean%20interface%20business&width=600&height=400&seq=304&orientation=landscape',
  },
];

const SoftwareCard = ({ sw, i }: { sw: typeof softwares[0]; i: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center transition-all duration-1000 ${
        visible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-12'
      }`}
    >
      {/* Image */}
      <div className={`${i % 2 === 1 ? 'lg:order-2' : ''}`}>
        <div className="rounded-2xl overflow-hidden w-full h-64 md:h-80 group">
          <img
            src={sw.image}
            alt={sw.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
        </div>
      </div>
      {/* Content */}
      <div className={`${i % 2 === 1 ? 'lg:order-1' : ''}`}>
        <div className={`w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${sw.color} mb-4 hover:scale-110 transition-transform duration-300`}>
          <i className={`${sw.logo} text-2xl text-white`}></i>
        </div>
        <span className="inline-block bg-orange-100 text-orange-600 text-xs font-semibold px-3 py-1 rounded-full mb-3">{sw.category}</span>
        <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3">{sw.name}</h2>
        <p className="text-slate-500 text-base leading-relaxed mb-5">{sw.desc}</p>
        <ul className="space-y-2 mb-6">
          {sw.features.map((f, fi) => (
            <li
              key={fi}
              className={`flex items-center gap-2 text-sm text-slate-700 transition-all duration-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
              style={{ transitionDelay: `${200 + fi * 80}ms` }}
            >
              <i className="ri-check-line text-orange-500"></i>
              {f}
            </li>
          ))}
        </ul>
        <div className="flex gap-3">
          <a href="https://hansoft.com.tr" target="_blank" rel="nofollow noopener noreferrer" className="bg-slate-900 hover:bg-orange-500 hover:scale-105 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2">
            Detaylı Bilgi <i className="ri-external-link-line"></i>
          </a>
          <a href="/iletisim" className="border border-slate-300 hover:border-orange-500 hover:scale-105 text-slate-700 hover:text-orange-500 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap">
            Demo İste
          </a>
        </div>
      </div>
    </div>
  );
};

const YazilimPage = () => {
  const { ref: heroRef, visible: heroVisible } = useScrollReveal(0.1);
  const { ref: bannerRef, visible: bannerVisible } = useScrollReveal(0.1);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero */}
      <div className="pt-16 md:pt-20 bg-slate-900 text-white py-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,165,0,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,165,0,0.3) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div
          ref={heroRef}
          className={`max-w-7xl mx-auto px-4 md:px-6 text-center relative z-10 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block bg-orange-500/20 text-orange-400 text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wider border border-orange-500/30">
            Yazılım Çözümleri
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4">İşletmenize Özel Yazılımlar</h1>
          <p className="text-slate-300 text-base max-w-xl mx-auto">HanSoft iş ortaklığıyla sunduğumuz profesyonel yazılım çözümleri ile işletmenizi dijitale taşıyın.</p>
        </div>
      </div>

      {/* Software List */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 space-y-16">
        {softwares.map((sw, i) => (
          <SoftwareCard key={i} sw={sw} i={i} />
        ))}
      </div>

      {/* HanSoft Banner */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <div
          ref={bannerRef}
          className={`bg-orange-50 border border-orange-200 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6 transition-all duration-700 ${bannerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-orange-500 flex-shrink-0">
            <i className="ri-information-line text-2xl text-white"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-slate-900 text-lg mb-1">HanSoft İş Ortaklığı</h3>
            <p className="text-slate-600 text-sm">Bu yazılımlar HanSoft Bilişim tarafından geliştirilmekte ve sunulmaktadır. Lisans, fiyat ve demo bilgisi için bizimle iletişime geçin.</p>
          </div>
          <a href="https://hansoft.com.tr" target="_blank" rel="nofollow noopener noreferrer" className="bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0">
            hansoft.com.tr
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default YazilimPage;
